import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useLang } from '../../../hooks/useLang';
import { useScrollProgress } from '../../../hooks/useScrollProgress';
import GlossaryQuiz from './GlossaryQuiz';

const CATEGORIES = [
  {
    id: 'regulator',
    name: '监管机构',
    icon: 'ri-government-line',
    color: '#DC2626',
    bg: 'bg-red-50',
    headerGrad: 'from-red-600 to-rose-500',
    border: 'border-red-200',
    tagBg: 'bg-red-100',
    tagText: 'text-red-700',
    cardBg: 'bg-white',
    cardBorder: 'border-red-100',
    accent: '#DC2626',
    desc: '全球主要金融监管机构，涵盖香港、内地、美国、欧盟、新加坡及国际组织',
  },
  {
    id: 'basic',
    name: '基础概念',
    icon: 'ri-seedling-line',
    color: '#059669',
    bg: 'bg-emerald-50',
    headerGrad: 'from-emerald-600 to-teal-500',
    border: 'border-emerald-200',
    tagBg: 'bg-emerald-100',
    tagText: 'text-emerald-700',
    cardBg: 'bg-white',
    cardBorder: 'border-emerald-100',
    accent: '#059669',
    desc: 'RWA底层概念体系，包含代币化、STO、底层资产、确权等核心基础术语',
  },
  {
    id: 'erc',
    name: 'ERC标准体系',
    icon: 'ri-code-box-line',
    color: '#4338CA',
    bg: 'bg-indigo-50',
    headerGrad: 'from-indigo-700 to-violet-600',
    border: 'border-indigo-200',
    tagBg: 'bg-indigo-100',
    tagText: 'text-indigo-700',
    cardBg: 'bg-white',
    cardBorder: 'border-indigo-100',
    accent: '#4338CA',
    desc: '以太坊ERC全谱系18个核心标准，含选型矩阵与碳资产RWA最佳组合方案',
  },
  {
    id: 'tech',
    name: '技术与代币',
    icon: 'ri-cpu-line',
    color: '#1D4ED8',
    bg: 'bg-blue-50',
    headerGrad: 'from-blue-700 to-cyan-600',
    border: 'border-blue-200',
    tagBg: 'bg-blue-100',
    tagText: 'text-blue-700',
    cardBg: 'bg-white',
    cardBorder: 'border-blue-100',
    accent: '#1D4ED8',
    desc: '智能合约、预言机、Layer2、联盟链、DID、ZKP等区块链核心技术术语',
  },
  {
    id: 'compliance',
    name: '合规与监管',
    icon: 'ri-shield-check-line',
    color: '#B45309',
    bg: 'bg-amber-50',
    headerGrad: 'from-amber-600 to-orange-500',
    border: 'border-amber-200',
    tagBg: 'bg-amber-100',
    tagText: 'text-amber-700',
    cardBg: 'bg-white',
    cardBorder: 'border-amber-100',
    accent: '#B45309',
    desc: 'KYC/AML、FATF旅行规则、MiCA、Reg D、尽职调查、托管人制度等合规核心词汇',
  },
  {
    id: 'institution',
    name: '机构与角色',
    icon: 'ri-building-2-line',
    color: '#C2410C',
    bg: 'bg-orange-50',
    headerGrad: 'from-orange-600 to-red-400',
    border: 'border-orange-200',
    tagBg: 'bg-orange-100',
    tagText: 'text-orange-700',
    cardBg: 'bg-white',
    cardBorder: 'border-orange-100',
    accent: '#C2410C',
    desc: 'VATP/VASP、SPV特殊目的载体、专业投资者等RWA生态核心角色概念',
  },
  {
    id: 'finance',
    name: '金融工具',
    icon: 'ri-line-chart-line',
    color: '#6D28D9',
    bg: 'bg-violet-50',
    headerGrad: 'from-violet-700 to-fuchsia-600',
    border: 'border-violet-200',
    tagBg: 'bg-violet-100',
    tagText: 'text-violet-700',
    cardBg: 'bg-white',
    cardBorder: 'border-violet-100',
    accent: '#6D28D9',
    desc: 'CBDC、NAV、ABS、REITs、碳信用、TVL、信托受益凭证等金融产品与工具',
  },
  {
    id: 'defi',
    name: 'DeFi与流动性',
    icon: 'ri-water-flash-line',
    color: '#0E7490',
    bg: 'bg-cyan-50',
    headerGrad: 'from-cyan-700 to-teal-500',
    border: 'border-cyan-200',
    tagBg: 'bg-cyan-100',
    tagText: 'text-cyan-700',
    cardBg: 'bg-white',
    cardBorder: 'border-cyan-100',
    accent: '#0E7490',
    desc: 'DeFi、流动性池、AMM、原子结算、稳定币、二级市场流动性等DeFi核心概念',
  },
];

const ERC_OVERVIEW = {
  what: 'ERC = Ethereum Request for Comments。以太坊社区提出的代币/合约/功能标准，是一套"通用接口规范"，让所有以太坊生态的钱包、交易所、DApp、智能合约都能互相兼容、互相识别、互相调用。ERC不是代币，是规则——任何人按规则写合约，就能发行对应资产。',
  why: [
    { icon: 'ri-plug-line', color: '#10B981', title: '兼容性', desc: '所有交易所、钱包、DeFi天然支持' },
    { icon: 'ri-shield-check-line', color: '#3B82F6', title: '安全性', desc: '经社区长期验证，减少致命漏洞' },
    { icon: 'ri-puzzle-2-line', color: '#8B5CF6', title: '可组合性', desc: '与DeFi/RWA/NFT任意组合的"金融乐高"' },
    { icon: 'ri-global-line', color: '#F59E0B', title: '跨链通用', desc: 'BSC/Polygon/Avalanche全兼容ERC标准' },
  ],
  matrix: [
    { erc: 'ERC-20', desc: '同质化代币（钱）', use: 'RWA份额、稳定币、碳信用', rwa: '★★★★★' },
    { erc: 'ERC-721', desc: 'NFT（唯一物品）', use: '房产NFT、碳项目NFT', rwa: '★★★★☆' },
    { erc: 'ERC-1155', desc: '多资产混合（钱+物）', use: '碳资产打包、批量发行', rwa: '★★★★★' },
    { erc: 'ERC-1400', desc: '证券型代币（合规）', use: 'STO、债券、股权', rwa: '★★★★★' },
    { erc: 'ERC-3643', desc: '新一代合规RWA', use: 'RWA/跨境合规发行', rwa: '★★★★★' },
    { erc: 'ERC-4626', desc: '金库标准（理财）', use: 'RWA收益池、碳资产分红', rwa: '★★★★☆' },
    { erc: 'ERC-3525', desc: '半同质化（可拆分NFT）', use: '碳资产分级、债券分期', rwa: '★★★★☆' },
    { erc: 'ERC-725', desc: '去中心化身份DID', use: 'KYC链上化、合规身份', rwa: '★★★☆☆' },
  ],
};

interface Term {
  id: string; en: string; category: string;
  def: string; scenario: string; compliance: string; example: string;
}

const TERMS: Term[] = [
  // ── 监管机构 ──
  {
    id: 'SFC', en: 'Securities and Futures Commission (HK)', category: 'regulator',
    def: '香港证券及期货事务监察委员会。监管香港证券市场、虚拟资产服务提供商（VASP）及STO发行，是RWA在港合规的核心监管机构。签发牌照类型：1类（经纪）、4类（顾问）、9类（资产管理）是RWA发行最常见三类。',
    scenario: '企业通过香港SPV发行RWA，须委托SFC持牌1类机构作为分销商，SFC持牌9类机构作为资产管理人，4类机构提供投资建议。',
    compliance: '必须使用SFC持牌机构。禁止通过无牌机构发行证券型代币，否则违反《证券及期货条例》第103条，最高刑罚：5年监禁+500万港元罚款。',
    example: 'SFC官网 www.sfc.hk 可查询所有持牌机构清单及牌照类型',
  },
  {
    id: 'HKMA', en: 'Hong Kong Monetary Authority', category: 'regulator',
    def: '香港金融管理局。监管银行体系与货币稳定，主导批发型CBDC（e-HKD）研发及Project Ensemble沙盒，探索代币化存款与RWA结算。是香港RWA基础设施建设的重要推手。',
    scenario: '汇丰、渣打等银行参与HKMA的Project Ensemble沙盒，测试代币化港元存款作为RWA交易结算货币，解决跨境结算难题。',
    compliance: 'HKMA持牌银行可提供代币化存款，但须获得银行牌照及HKMA相关授权，普通科技公司不得发行代币化存款。',
    example: 'HKMA官网 www.hkma.gov.hk 定期发布Project Ensemble沙盒进展报告',
  },
  {
    id: 'CSRC', en: 'China Securities Regulatory Commission', category: 'regulator',
    def: '中国证券监督管理委员会。境内证券市场主监管机构。对于RWA跨境发行，境内资产须先获证监会备案许可，取得"跨境资产证券化备案函"后才能通过香港合规通道在境外发行。',
    scenario: '境内商业地产资产进行RWA化，发起人须向证监会提交资产证明、尽职调查报告、SPV架构说明，经备案后取得函件，作为香港SFC审核的前置文件。',
    compliance: '未经证监会备案擅自将境内资产进行境外代币化，违反《证券法》第二条，可被认定为非法发行证券，面临行政处罚及刑事责任。',
    example: '证监会官网 www.csrc.gov.cn 发布跨境资产证券化相关指引（持续更新中）',
  },
  {
    id: 'PBOC', en: "People's Bank of China", category: 'regulator',
    def: '中国人民银行。中国货币当局与金融宏观审慎监管机构。主导e-CNY（数字人民币）研发，是2021年十部委"全面禁止虚拟货币"通知的牵头发布单位。外汇管制与境外资金回流合规通道均由人行监管。',
    scenario: 'RWA资金经QFLP或CIPS通道从香港回流境内时，须符合人行外汇管理要求，提交资金来源证明及投资合同，经所在地外管局审批。',
    compliance: '跨境资金流动须严格遵守人行及国家外汇管理局规定，不得绕开资本管制。违规将面临外汇处罚并触发反洗钱调查。',
    example: '人民银行官网 www.pbc.gov.cn，e-CNY试点报告及外汇监管文件均可下载',
  },
  {
    id: 'NFRA', en: 'National Financial Regulatory Administration', category: 'regulator',
    def: '国家金融监督管理总局（前身：银保监会）。监管银行、保险机构。2021年通知的联合发布单位之一。禁止境内银行及保险公司为虚拟货币相关业务提供账户、结算及保险服务，直接切断境内RWA的金融基础设施通道。',
    scenario: '境内银行收到企业要求为"区块链项目"开设专户，须核查项目是否涉及虚拟货币或代币发行，若涉及则依照NFRA指令拒绝开户。',
    compliance: '境内银行不得为RWA代币发行相关业务开立账户或结算，金融机构违规将被NFRA处以罚款并吊销相关业务资质。',
    example: 'NFRA官网 www.nfra.gov.cn 发布银行虚拟货币相关业务禁止通知',
  },
  {
    id: 'SEC', en: 'U.S. Securities and Exchange Commission', category: 'regulator',
    def: '美国证券交易委员会。主张绝大多数加密资产（含RWA代币）属于证券，须依《证券法》注册或豁免。通过"豪威测试"判断资产是否为证券：具有金钱投入+共同事业+预期利润+依赖他人努力四要素则为证券。',
    scenario: '美国RWA项目通常选择Reg D 506(c)豁免（仅限合格投资者，无金额上限）或Reg A+豁免（可向公众发行，上限7500万美元/年）绕开完整注册程序。',
    compliance: '未经SEC注册或豁免向美国居民发行RWA代币属联邦证券法违规。SEC对跨境发行具有管辖权，向美国居民宣传未经注册证券同样违规。',
    example: 'SEC已对多个RWA/DeFi项目展开调查，包括BlockFi（罚款1亿美元）、Nexo等',
  },
  {
    id: 'MAS', en: 'Monetary Authority of Singapore', category: 'regulator',
    def: '新加坡金融管理局。新加坡综合金融监管机构（集央行、证监、银监职能于一体）。主导Project Guardian RWA沙盒，制定《数字代币发行指南》，将代币分为支付型/证券型/实用型，对证券型代币要求申领CMS牌照。',
    scenario: '在新加坡发行房地产RWA代币，须向MAS申领资本市场服务牌照（CMS License），并确认代币是否构成证券（依《证券与期货法》Section 2(1)定义）。',
    compliance: '未经MAS批准向新加坡居民发行证券型代币属违法，MAS对无牌机构处以高额罚款并可提起刑事诉讼。',
    example: 'MAS Project Guardian：JP摩根、DBS、星展等参与，官网 www.mas.gov.sg',
  },
  {
    id: 'ESMA', en: 'European Securities and Markets Authority', category: 'regulator',
    def: '欧洲证券与市场管理局。欧盟证券监管最高机构，与各成员国监管机构协调执行MiFID II、MiCA等欧盟法规。明确证券型RWA代币不受MiCA管辖，而须在MiFID II框架（DLT MTF）下交易和结算。',
    scenario: '欧洲资产管理公司在法国发行房地产RWA代币，须向法国AMF申领CASP牌照或DLT MTF牌照，须经法律意见书确认代币法律定性。',
    compliance: 'MiCA vs MiFID II双轨并行：发行前须明确代币法律定性。证券型代币使用MiCA路径属违规，须经法律意见书确认。',
    example: 'ESMA官网 www.esma.europa.eu 发布DLT市场基础设施试点监管技术标准（RTS）',
  },
  {
    id: 'BIS', en: 'Bank for International Settlements', category: 'regulator',
    def: '国际清算银行。全球中央银行的中央银行，制定全球金融稳定标准。发布多份关于代币化、RWA、CBDC的权威研究报告，其研究成果是各国央行设计监管框架的重要参考。',
    scenario: 'BIS报告《代币化与统一账本》（2023）提出"统一账本"概念，影响多国央行对CBDC与RWA结算基础设施的设计思路。',
    compliance: 'BIS本身不直接监管金融机构，但其发布的标准（如巴塞尔协议）被各国监管机构强制执行。加密资产领域巴塞尔III要求银行对1类加密资产1:1计提准备金。',
    example: 'BIS官网 www.bis.org 可免费下载全部RWA、代币化、CBDC研究报告',
  },
  // ── 基础概念 ──
  {
    id: 'RWA', en: 'Real World Assets', category: 'basic',
    def: '真实世界资产。指现实世界中的有形（如房地产、黄金、大宗商品）或无形（如债权、版权、碳信用）资产，通过区块链代币化技术转化为可在链上流通的数字代币的总称。',
    scenario: '房地产开发商将商业大楼未来租金收益拆分为10万份代币，通过RWA平台向全球专业投资者发售，每份代币代表1/10万的收益权。',
    compliance: '境内严禁发行RWA代币。合法路径：境内资产确权→证监会备案→香港SPV→SFC持牌机构发行→仅限专业投资者认购。',
    example: 'Blackrock BUIDL（美债RWA，规模$10亿+）、HSBC Orion（香港绿色债券，HK$8亿）',
  },
  {
    id: '代币化', en: 'Tokenization', category: 'basic',
    def: '将真实资产的权利（所有权、收益权、使用权）转化为区块链上的数字代币的过程。代币化使资产具备可拆分性、可编程性和7×24全球可流通性，是RWA的核心技术动作。',
    scenario: '一幅价值1亿港元的画作经代币化为100万个ST代币，每份代表1/100万的所有权及未来升值收益权，全球买家通过持牌平台购买。',
    compliance: '代币化本身技术中性，但发行具有投资性质的代币须受证券法监管，须持牌发行。',
    example: 'Securitize为KKR私募基金代币化，投资者可从100美元起投',
  },
  {
    id: 'STO', en: 'Security Token Offering', category: 'basic',
    def: '证券型代币发行。将符合证券监管定义的资产（股权、债权等）以代币形式在合规框架下向投资者发行，是ICO的合规升级版，须受各地证监会监管。',
    scenario: '香港SPV持有内地商业地产收益权，通过SFC持牌机构发行STO，面向香港专业投资者认购，每份代币代表特定比例的租金收益权。',
    compliance: '须经SFC持牌机构承销，仅限香港专业投资者（个人净资产≥800万港币），须提供招股说明书，不得向内地投资者发售。',
    example: '中银国际BOCI发行2.28亿港元数字化结构性票据（2023年，以太坊上）',
  },
  {
    id: '底层资产', en: 'Underlying Asset', category: 'basic',
    def: '支撑RWA代币价值的真实物理或法律资产，如具体的商业地产、应收账款、国债组合、黄金库存等。底层资产的质量、流动性和法律权属清晰程度，决定了对应RWA代币的投资价值。',
    scenario: 'Ondo Finance OUSG代币的底层资产是BlackRock短期国债ETF（iShares），代币价值与该ETF的NAV直接挂钩。',
    compliance: '底层资产须由独立法律机构出具权属清晰的法律意见书；若为境内资产须完成国内权属登记，再经合法通道转让给香港SPV持有。',
    example: 'Franklin Templeton BENJI底层资产：美国政府基金；Centrifuge底层资产：企业应收账款',
  },
  {
    id: '资产确权', en: 'Asset Title Confirmation', category: 'basic',
    def: '在代币化前对底层资产进行的法律权属认定与登记程序。确权是RWA发行的第一步，须证明发行人拥有或有权处置该资产，且该资产不存在抵押纠纷、产权争议或法律瑕疵。',
    scenario: '内地商业地产进行RWA化，须先在不动产登记中心确认产权无抵押、无查封，再由律师出具产权无瑕疵确认函，才能将收益权合法转让给香港SPV。',
    compliance: '未确权资产进行代币化属于虚假发行。若底层资产存在法律纠纷，代币持有人的权益无法得到保障，发行方须承担欺诈责任。',
    example: '湖北区块链不动产登记平台实现链上不动产确权存证（全国首例）',
  },
  {
    id: '分级代币', en: 'Tranche Token', category: 'basic',
    def: 'RWA中将同一底层资产划分为优先级（Senior）和劣后级（Junior）代币，前者风险低收益低，后者风险高收益高，满足不同投资者风险偏好，是结构化金融的链上实现。',
    scenario: 'Centrifuge在Tinlake协议中将应收账款池分为TIN（劣后）和DROP（优先）代币，MakerDAO主要购买优先级DROP以降低风险。',
    compliance: '分级结构须在招股说明书中如实披露各级别的风险特征，确保优先级持有者在清算时优先获偿，结构不合理将被认定为欺诈。',
    example: 'Centrifuge Tinlake DROP/TIN双层结构、Goldfinch Senior/Junior Pool',
  },
  {
    id: '真实出售', en: 'True Sale', category: 'basic',
    def: '发起人将底层资产完全转让给SPV的法律行为，须确保资产在法律上与发起人破产隔离。"真实出售"是RWA结构中保护代币持有人的核心法律安排，须经独立律师出具"真实出售意见书"确认。',
    scenario: '企业将应收账款"真实出售"给SPV后，即使发起方企业破产，该应收账款仍属于SPV资产，代币持有人的权益不受发起方破产影响。',
    compliance: '若法院认定转让为"伪装出售"（实际上仍受发起方控制），则破产隔离失效，代币持有人将面临资产损失。真实出售意见书是香港SFC审批的必要文件。',
    example: '几乎所有RWA/ABS发行均需独立律师出具真实出售意见书，美国Reg D豁免发行尤为严格',
  },
  // ── ERC标准体系 ──
  {
    id: 'ERC-20', en: 'Fungible Token Standard', category: 'erc',
    def: '以太坊同质化代币标准。所有代币完全相同、可拆分、可互换（1枚=1枚）。定义了transfer/approve/allowance三个核心接口，是以太坊代币的最基础规范。RWA份额代币、稳定币、碳信用均以此为基础。',
    scenario: 'USDC（美元稳定币）、UNI（Uniswap治理代币）、碳信用份额代币均采用ERC-20标准。RWA平台发行"1枚代币=1/10000商业地产收益权"时，代币本身即为ERC-20。',
    compliance: 'ERC-20本身无合规控制（任何人可转账），RWA场景须叠加合规层（如ERC-1400/ERC-3643）才能满足KYC/白名单要求。单独使用ERC-20发行RWA代币不满足SFC合规要求。',
    example: 'USDT/USDC/LINK/UNI均为ERC-20代币；Ondo Finance的OUSG底层采用ERC-20并叠加Securitize合规层',
  },
  {
    id: 'ERC-721', en: 'Non-Fungible Token Standard (NFT)', category: 'erc',
    def: '以太坊非同质化代币（NFT）标准。每个代币唯一、不可拆分、不可互换——编号#1的代币与编号#2的代币价值可以完全不同。适合表示"独一无二"的资产：单幢房产、单个碳项目、版权证书。',
    scenario: '某开发商将北京某商业大楼代币化为1枚ERC-721 NFT，链上记录完整产权信息和合规文件；碳项目NFT化：每个独立碳减排项目铸造为唯一NFT。',
    compliance: '证券属性ERC-721须纳入SFC合规框架，但NFT本身定性复杂：香港SFC尚未明确所有NFT为证券，须逐案评估。若NFT带有投资回报预期，大概率被认定为证券型代币。',
    example: 'RealT早期用ERC-721发行单个房产代币；NBA Top Shot、CryptoPunks是最著名ERC-721 NFT',
  },
  {
    id: 'ERC-1155', en: 'Multi-Token Standard', category: 'erc',
    def: '以太坊多资产混合标准。一个合约可同时管理无数种同质化（FT）+非同质化（NFT）资产，支持批量转账，Gas效率远高于ERC-20/ERC-721。是碳资产RWA打包、游戏道具、多类资产组合的首选标准。',
    scenario: '某碳资产RWA平台用1个ERC-1155合约管理：①云南林业碳汇批量单位（同质化，1000万吨可拆）；②独立项目认证NFT（非同质化，每个项目唯一）；③投资者持仓凭证。',
    compliance: '若ERC-1155代币具有投资收益预期，须在SFC合规框架下发行。批量铸造需确保每种代币类型的底层资产均已完成真实出售和确权。',
    example: 'Enjin游戏资产、OpenSea支持的多类NFT；碳资产RWA中Toucan Protocol使用ERC-1155管理批量碳信用',
  },
  {
    id: 'ERC-721A', en: 'Batch NFT Minting Standard', category: 'erc',
    def: 'ERC-721的Gas优化版本，由Azuki项目团队开发。支持批量铸造（mint）多个NFT只需支付接近单个NFT的Gas费。适合需要大批量铸造NFT的RWA项目（如一次发行10000个碳项目认证NFT）。',
    scenario: '碳资产RWA项目需一次性铸造5000枚碳项目认证NFT，使用ERC-721A批量铸造节省约90%的Gas费（节省数万美元部署成本）。',
    compliance: '与ERC-721合规要求相同。ERC-721A是纯粹的Gas优化，不增加任何合规功能，须叠加白名单/KYC层。',
    example: 'Azuki、BAYC衍生品等大型NFT项目广泛采用ERC-721A进行批量铸造',
  },
  {
    id: 'ERC-1400', en: 'Security Token Standard', category: 'erc',
    def: '以太坊证券型代币标准。在ERC-20基础上增加KYC白名单、权限分区、锁仓时间、合规文件存储、可暂停转账等安全证券所需功能。是RWA/STO最广泛采用的早期合规规范，被香港、新加坡多个STO项目使用。',
    scenario: 'RWA平台使用ERC-1400发行商业地产代币，合约自动拒绝向未通过KYC认证的钱包地址转账，仅白名单内的合格投资者可持有和流转代币。',
    compliance: 'ERC-1400内置合规控制大幅降低合规运营成本，但仍须取得相应监管牌照。ERC-1400合规合约须经独立安全审计，审计报告须向投资者公开。',
    example: 'BOCI数字票据（2023）、Securitize平台早期代币、tZERO证券代币交易所均采用ERC-1400标准',
  },
  {
    id: 'ERC-1404', en: 'Simple Restricted Token Standard', category: 'erc',
    def: '简单合规代币标准。在ERC-20上增加"转账前自动检查"机制，通过restrictionCode返回转账被禁止的原因（如：目标地址不在白名单、持有上限已满、转让锁定期内）。比ERC-1400更轻量，适合监管要求相对简单的场景。',
    scenario: '某企业债权RWA使用ERC-1404，设定：①每个投资者最多持有总供应量的5%；②转账须等待7天冷静期；③仅新加坡/香港注册钱包可接收。三条规则全部写入合约自动执行。',
    compliance: '适合监管要求明确且较简单的RWA发行，复杂的机构级合规场景建议升级为ERC-3643。',
    example: 'Tokensoft平台、Harbor协议采用ERC-1404实现简单合规股权代币',
  },
  {
    id: 'ERC-1594', en: 'Core Security Token Standard', category: 'erc',
    def: '核心证券代币标准，是ERC-1400系列的"发行/转账/强制赎回"模块。定义了标准化的代币发行（issuance）、转账限制（transferWithData）、强制赎回（redeem）接口，使不同平台的证券代币具有统一操作方式。',
    scenario: '香港STO项目使用ERC-1594定义代币赎回接口，当底层资产到期时，发行方调用redeem()函数向所有持有人自动分配赎回金额并销毁对应代币，流程完全链上透明。',
    compliance: 'ERC-1594的强制赎回（forceRedeem）功能须符合SFC要求，不得用于未经授权的强制没收。使用前须经法律顾问确认合规边界。',
    example: 'Polymath（证券代币发行平台）是ERC-1400/1594的最大推动者，已协助发行50+证券型代币',
  },
  {
    id: 'ERC-3643', en: 'T-REX Protocol', category: 'erc',
    def: '新一代合规RWA代币协议（T-REX），由Tokeny Solutions开发。内置链上身份注册表（Identity Registry）+合规模块（Compliance Module）。身份注册表存储每个投资者的链上身份（OnchainID），合规模块自动执行国家限制、持仓上限、锁定期等规则，是目前RWA领域最推荐的合规架构。但不取代监管许可：合约本身须独立安全审计，发行方仍须持牌。',
    scenario: 'Tokeny帮助欧洲资产管理公司发行符合MiFID II的RWA代币：法国投资者须额外通过法国AMF要求的KYC；德国投资者有不同持仓上限；中国居民自动被排除——全部写入合规模块自动执行。',
    compliance: 'ERC-3643通过链上合规逻辑大幅降低手动合规成本，是SFC、MAS等主要监管机构认可的合规架构。但不取代监管许可：合约本身须独立安全审计，发行方仍须持牌。',
    example: 'Tokeny平台已服务100+合规RWA发行；欧洲多家资产管理公司（BNP Paribas资产管理）采用ERC-3643',
  },
  {
    id: 'ERC-3525', en: 'Semi-Fungible Token (SFT)', category: 'erc',
    def: '半同质化代币标准。结合ERC-20（可拆分）和ERC-721（唯一性）的特性：每个代币有独特ID（非同质化），但同一"槽位"（Slot）内的代币可以相互拆分和合并（同质化）。适合表示债券（不同期限/利率）、碳资产分级、票据分层。',
    scenario: '债券RWA：发行100亿债券，拆分为3档（3年期/5年期/10年期），每档内部可任意拆分。使用ERC-3525：3档对应3个Slot，每个Slot内的代币可自由拆分，不同Slot间不可互换。',
    compliance: '半同质化代币须确认每个Slot的代币性质（是否构成证券），可能需要分别进行证券定性分析。同一合约内不同Slot可能受不同监管规则管辖。',
    example: 'Solv Protocol（债券/票据SFT）是ERC-3525最大应用平台；碳资产分级代币化也开始使用ERC-3525',
  },
  {
    id: 'ERC-4626', en: 'Tokenized Vault Standard', category: 'erc',
    def: '代币化金库标准。标准化"存入基础资产→获得份额代币→赎回底层资产"的流程，定义统一的deposit/withdraw/convertToAssets/convertToShares接口。RWA收益池（如美债收益池）的最佳标准：份额代币随底层资产价值自动增值。',
    scenario: 'RWA美债收益池使用ERC-4626：投资者存入USDC，获得份额代币（如fUSDC）；底层资产购买美债后开始计息；fUSDC余额保持不变，但每枚fUSDC可兑换的USDC数量每日增加（反映利息）。',
    compliance: '若金库持有证券型底层资产（如美国国债），ERC-4626金库本身须受证券法监管，不得作为绕开投资者适当性的工具。须向SFC证明份额代币的证券属性已纳入合规框架。',
    example: 'Ondo Finance OUSG使用ERC-4626金库；yearn Finance、Aave V3均采用ERC-4626标准化金库接口',
  },
  {
    id: 'ERC-4907', en: 'Rental NFT Standard', category: 'erc',
    def: 'NFT租赁标准。将NFT的"所有权（Owner）"与"使用权（User）"分离，Owner保留所有权，可将使用权租赁给User直至指定到期时间，到期自动归还。适合碳信用租赁、版权使用权出租、商业地产使用权分离。',
    scenario: '企业A需要在Q4财报前完成1万吨碳抵消义务，但不想长期持有碳信用资产。通过ERC-4907租赁他人持有的碳信用NFT，使用期限3个月，租期结束后自动归还，支付租金（以稳定币计）。',
    compliance: '碳信用租赁须确保底层碳信用的实际注销（避免"租而不销"的双重计算问题）。监管机构对"租赁碳信用"用于年度报告的接受度仍在讨论中。',
    example: 'RMRK协议、Double Protocol支持ERC-4907；碳信用租赁是ERC-4907最受关注的RWA应用场景',
  },
  {
    id: 'ERC-777', en: 'Advanced Token Standard', category: 'erc',
    def: 'ERC-20的高级升级版。引入"Hook（钩子）"机制：代币被发送时，接收方合约可自动触发逻辑（如自动质押、自动购买）；支持可销毁（burn）、可暂停操作符（operator）机制。适合需要复杂自动化逻辑的合规代币。',
    scenario: 'RWA平台使用ERC-777：当投资者收到租金分红代币时，其钱包合约自动触发"再投资"逻辑，将分红重新购买底层资产份额——全程链上自动化。',
    compliance: 'ERC-777的Hook机制曾导致重入攻击漏洞（imBTC事件，2020年损失约25万美元），使用前须经严格安全审计。DeFi协议已普遍对ERC-777提高警惕。',
    example: 'GNO（Gnosis）早期使用ERC-777；多数新项目已转向ERC-20+ERC-3643组合代替ERC-777',
  },
  {
    id: 'ERC-2612', en: 'Permit Extension for ERC-20', category: 'erc',
    def: 'ERC-20的签名授权扩展。通过链下签名代替链上approve交易，用户无需支付Gas费即可完成授权。在RWA场景下，用户签名授权平台代为操作代币（如分红再投资、赎回）而不需要持有ETH支付Gas。',
    scenario: '机构投资者持有大量RWA代币，需要授权平台进行NAV计算和定期分红。使用ERC-2612：投资者用私钥签名（类似电子签章），平台获得授权后代为操作——投资者无需理解Gas机制。',
    compliance: '签名授权须设置合理的过期时间（deadline）和使用限额（value），防止授权被滥用。签名授权不等于平台可自由处置资产，须在用户协议中明确限制范围。',
    example: 'USDC（Circle）内置ERC-2612 permit；Uniswap V3、Aave V3均支持permit签名授权',
  },
  {
    id: 'ERC-1363', en: 'Payable Token Standard', category: 'erc',
    def: '支付后回调标准。扩展ERC-20，使代币转账后可自动触发接收方合约的逻辑（transferAndCall）。在RWA场景中，投资者付款的同时自动触发认购逻辑，无需两步操作（先approve再调用合约）。',
    scenario: 'RWA认购流程优化：投资者使用USDC调用transferAndCall()，一笔交易同时完成：①USDC转账到发行方账户；②触发发行合约铸造等值RWA代币到投资者钱包——传统两步操作变一步完成。',
    compliance: '回调机制须防范重入攻击，须采用Checks-Effects-Interactions模式和ReentrancyGuard保护。使用前经CertiK等机构安全审计。',
    example: 'ERC-1363是多个RWA支付优化方案的技术选型，PayToken标准的商业化实现',
  },
  {
    id: 'ERC-2981', en: 'NFT Royalty Standard', category: 'erc',
    def: 'NFT版税标准。定义了NFT版权方在二次交易时自动收取版税的标准接口：每次NFT在二级市场交易时，智能合约自动将约定比例（如5%）的交易金额转给版权方，无需版权方手动操作。',
    scenario: '艺术家将画作代币化为NFT（采用ERC-2981），设定10%版税。10年后该画作NFT在二级市场以1000万港元成交，100万港元自动转入艺术家钱包——无论画作转手多少次，版权方永久受益。',
    compliance: '版税分配须与原版权合同约定一致，链上版税不能替代纸质版权协议。若NFT代表真实版权须经知识产权律师确认链上权利与法律权利的一致性。',
    example: 'OpenSea、Rarible、Foundation等主要NFT市场均支持ERC-2981；Bored Ape Yacht Club采用10%版税',
  },
  {
    id: 'ERC-725', en: 'Blockchain-based Account & Key Manager', category: 'erc',
    def: '去中心化数字身份标准。定义了链上通用账户（可存储任意键值对数据）和身份声明（Claims）的标准接口。ERC-725+ERC-735组合实现完整的链上DID身份系统：个人/机构在链上注册身份，由受信任方（如KYC机构）颁发"已完成KYC"等声明。',
    scenario: '投资者向Tokeny持牌KYC合作伙伴完成身份验证后，其ERC-725链上身份账户收到"香港专业投资者资质有效"的声明（Claim）。此后在任何采用ERC-3643/ERC-725标准的RWA平台均无需重复KYC——"一次验证，全网通行"。',
    compliance: 'ERC-725身份须由受监管的KYC机构颁发声明才具合规效力。链上DID不能自行声明，须通过SFC认可的合规机构背书。GDPR要求须有"被遗忘权"机制须通过存储加密或Off-chain存储解决。',
    example: 'OnchainID（Tokeny）是ERC-725最主要的商业实现，服务Tokeny T-REX平台的所有身份管理',
  },
  {
    id: 'ERC-1238', en: 'Non-Transferable Token (SBT)', category: 'erc',
    def: '不可转让代币标准（灵魂绑定代币的早期实现方向）。代币一旦发放给某地址就无法转移，永久与钱包绑定。适合：合规资质凭证（不可转卖"专业投资者"资格）、碳履约凭证（企业完成减排义务的不可转让证明）。',
    scenario: '企业完成年度碳中和后，监管机构颁发一枚ERC-1238"碳中和认证凭证"至企业钱包，该凭证不可转让、不可出售、永久链上可查——任何人可验证该企业确实完成了认证，且认证不可被伪造或转移。',
    compliance: '不可转让设计天然防止合规资质"倒卖"。但须确认智能合约中的转让限制无法被绕过（须防范合约升级时取消限制的风险）。',
    example: 'Vitalik Buterin提出的SoulBound Token（SBT）概念延伸了ERC-1238；Proof of Attendance Protocol (POAP) 类似实现',
  },
  {
    id: 'ERC-2771', en: 'Meta-Transaction Standard', category: 'erc',
    def: '元交易标准（Gas代付）。允许第三方（如RWA平台）代替用户支付Gas费，用户只需签名授权，平台承担Gas成本。极大降低非加密原生投资者的使用门槛——投资者无需持有ETH即可交互RWA智能合约。',
    scenario: '某企业高管想购买RWA代币，但不了解如何购买ETH支付Gas。平台采用ERC-2771：高管只需用私钥签名（类似电子签章），平台代付Gas，从机构钱包完成认购——与传统电子银行体验相同，区块链对用户完全透明。',
    compliance: '代付Gas的受信任转发器（Trusted Forwarder）须有严格权限控制，防止恶意代替用户提交错误交易。须向用户清晰披露平台代付Gas的收费安排。',
    example: 'OpenGSN（Gas Station Network）是ERC-2771的最主要基础设施；Biconomy提供企业级Gas代付服务',
  },
  // ── 技术与代币 ──
  {
    id: '智能合约', en: 'Smart Contract', category: 'tech',
    def: '部署在区块链上的自动执行代码。当预设条件满足时自动执行，无需人工干预。在RWA中负责自动分红、KYC白名单管理、转让限制、赎回计算等核心功能。',
    scenario: '每月房租到账后，智能合约自动按代币持有比例将租金分配给所有持有人，无需任何中间人，T+0即时完成。',
    compliance: '智能合约代码须经第三方审计（CertiK、Hacken等），审计报告须向投资者公开。合约Bug导致的损失通常无法追回。',
    example: 'Centrifuge使用智能合约自动管理应收账款池的融资和还款',
  },
  {
    id: '联盟链', en: 'Consortium / Permissioned Blockchain', category: 'tech',
    def: '仅由特定联盟成员共同维护的许可型区块链。境内合规区块链技术路径，代表性平台包括蚂蚁链、长安链、FISCO BCOS、BSN等。联盟链须经工信部区块链信息服务备案，不发行代币。',
    scenario: '某供应链金融公司在蚂蚁链上将应收账款上链存证，用于向银行申请融资，全程不发行任何代币，仅利用区块链不可篡改特性提升数据可信度。',
    compliance: '境内联盟链须完成"区块链信息服务备案"（网信办），禁止在联盟链上发行虚拟货币或代币。联盟链存证数据可作为法院认可的电子证据。',
    example: '长安链（北京市政府）、蚂蚁链（阿里）、FISCO BCOS（微众银行）、BSN（国家区块链新型基础设施）',
  },
  {
    id: '预言机', en: 'Oracle', category: 'tech',
    def: '区块链与现实世界的数据桥梁。将链下的资产价格、合规状态、利率等数据实时输入链上智能合约。Chainlink是最主要的去中心化预言机，Band Protocol、Pyth也广泛使用。',
    scenario: 'RWA黄金代币合约通过Chainlink预言机实时获取伦敦金市黄金价格，自动计算每枚代币清算价值，确保价格透明可信。',
    compliance: '预言机数据来源须公开透明，Oracle Manipulation（价格预言机操纵）是DeFi重大安全风险，须多源数据验证。',
    example: 'Chainlink为Ondo Finance的OUSG国债代币提供实时国债价格数据',
  },
  {
    id: 'Layer2', en: 'Layer 2 Scaling Solution', category: 'tech',
    def: '以太坊二层扩展方案。在以太坊主链之上构建的高速低费交易层，解决主链TPS低、Gas费贵的问题。RWA常用的L2有Polygon、Arbitrum、Optimism等。',
    scenario: 'RWA发行商选择Polygon而非以太坊主网，每次代币转让Gas费仅$0.01而非$10-50，大幅提升流通体验。',
    compliance: 'L2须通过智能合约审计，且须确认L2的最终结算安全性（finality）。部分监管机构对L2代币法律定性尚待明确。',
    example: 'Franklin Templeton的Benji基金在Polygon发行，RealT在Gnosis Chain发行',
  },
  {
    id: 'DID', en: 'Decentralized Identity', category: 'tech',
    def: '去中心化数字身份。用户自主控制的链上数字身份标识，可在不同平台间互通，不依赖中心化机构认证。在RWA合规体系中，DID可与KYC结果绑定，实现"一次认证、全平台通行"。',
    scenario: '投资者在某持牌KYC服务商完成身份认证后，获得绑定DID的"合格投资者凭证"，在支持该DID标准的所有RWA平台均可直接投资，无需重复KYC。',
    compliance: 'DID须符合W3C DID标准，且凭证须由受认可机构签发。DID本身不代表完成监管要求的KYC，须与持牌机构合作的KYC流程结合。',
    example: 'Polygon ID、Ceramic Network、Ontology均提供DID解决方案；欧盟eIDAS 2.0计划引入DID标准',
  },
  {
    id: 'ZKP', en: 'Zero-Knowledge Proof', category: 'tech',
    def: '零知识证明。密码学技术，允许一方向另一方证明某陈述为真，而无需透露任何额外信息。在RWA合规中，ZKP可实现"证明投资者是合格投资者，但无需向平台暴露具体资产数额"。',
    scenario: '投资者使用ZKP向RWA平台证明：本人净资产≥800万港元（合格投资者门槛），而无需向平台提交完整的银行流水，保护隐私同时满足合规要求。',
    compliance: 'ZKP是隐私增强技术，不豁免KYC法规要求。使用ZKP的合规方案须经监管机构确认其接受ZKP证明作为合规文件的替代方案。',
    example: 'zkKYC是区块链隐私合规的新兴方向，Polygon zkEVM、StarkNet均支持ZKP计算',
  },
  {
    id: 'EVM', en: 'Ethereum Virtual Machine', category: 'tech',
    def: '以太坊虚拟机。以太坊智能合约运行的沙盒执行环境，所有节点以完全相同的方式运行合约代码，确保结果一致性。EVM是"以太坊兼容"的技术基础，Polygon、BNB Chain、Avalanche等链都兼容EVM，意味着同一套合约代码可多链部署。HashKey Chain（香港合规RWA专用L2）也是EVM兼容链，是SFC持牌机构运营的合规公链L2。',
    scenario: 'RWA发行商用Solidity（EVM语言）编写一套智能合约代码，可同时部署在以太坊主网（高安全性）、Polygon（低费用）、HashKey Chain（香港合规专用）和Arbitrum（高性能），覆盖不同需求场景。',
    compliance: 'EVM合约是开源透明的，任何人可在Etherscan上查看合约代码。发行方须确保合约代码经过审计，无后门函数（如自毁、资金转走等）。',
    example: 'Solidity是EVM的主要编程语言，主流EVM兼容链包括以太坊、Polygon、Arbitrum、BNB Chain、Avalanche、HashKey Chain（香港合规L2）',
  },
  {
    id: '哈希值', en: 'Hash Value / Cryptographic Hash', category: 'tech',
    def: '哈希函数将任意长度数据转化为固定长度字符串的输出。区块链使用SHA-256（比特币）或Keccak-256（以太坊）算法。哈希值的三大特性：①单向性（无法从哈希值反推原始数据）；②唯一性（改一个字符，哈希值完全不同）；③确定性（相同输入永远得到相同输出）。这三个特性是区块链"不可篡改"的数学基础。',
    scenario: 'RWA合同文件存证：将200页法律合同的SHA-256哈希值（仅64个字符）上链，相当于给合同做了"数字公证"。任何人可重新计算合同哈希与链上记录对比，立刻发现合同是否被篡改——无需上链合同全文，保护商业秘密。',
    compliance: '合同哈希存证在香港和内地均被司法机构认可为电子证据。浙江/广州互联网法院已多次采信区块链哈希存证作为民事诉讼证据，RWA底层资产确权文件采用哈希存证是标准合规操作。',
    example: '蚂蚁链累计存证哈希超10亿条，覆盖司法、商业合同、知识产权；BOCI数字票据的合同文件均进行哈希存证',
  },
  {
    id: '默克尔树', en: 'Merkle Tree', category: 'tech',
    def: '一种树状数据结构，叶节点存储交易哈希，父节点存储两个子节点哈希的再哈希，最终汇聚到一个根节点（Merkle Root）。允许用少量数据（约13个哈希值的"证明路径"）验证某笔交易是否包含在包含上万笔交易的区块中。是轻节点验证、Layer2证明的核心数学工具。',
    scenario: 'HashKey Chain（L2）每小时将数千笔RWA交易打包成一个Merkle Root，提交到以太坊主链。以太坊主链只存储Root（32字节），而不是全部交易。SFC合规审计时，可通过Merkle Proof验证某笔特定RWA代币转让确实发生——一个Root证明一切。',
    compliance: 'Merkle Tree是Layer2解决方案（如HashKey Chain）的安全基础，其Merkle Root提交到以太坊主链后，L2交易历史与主链安全等级相同，SFC可视之为以太坊级别的安全保障。',
    example: '比特币SPV验证、以太坊MPT（Merkle Patricia Trie）、Layer2的状态根提交均基于Merkle Tree；IPFS内容寻址也使用Merkle DAG',
  },
  {
    id: '私钥与公钥', en: 'Private Key & Public Key', category: 'tech',
    def: '非对称加密密钥对。私钥：256位随机数，是区块链账户的绝对控制权凭证，丢失即永久失去资产控制权，SFC要求RWA代币私钥须由持牌机构独立托管。公钥：由私钥通过椭圆曲线算法（ECDSA）推导，可公开，用于验证签名。钱包地址：公钥经哈希运算后取后20字节，是对外展示的账户标识。',
    scenario: '机构投资者认购RWA代币后，代币存储在其专属钱包地址（形如0x742d...）。该地址的私钥由HashKey Custody保管在HSM硬件安全模块中，投资者无需自己管理私钥——类似银行保管保险柜钥匙，但投资者保留对资产的法律所有权。',
    compliance: 'SFC通函明确：证券型RWA代币的私钥须由独立持牌托管机构保管，发行人和投资者均不得自行保管。这是防范发行人卷款跑路和投资者私钥丢失的核心制度安排。多重签名（MultiSig）是机构私钥管理的行业标准。',
    example: 'HashKey Custody（香港持牌）、Fireblocks（机构级MPC私钥管理）、Ledger Enterprise均提供RWA私钥托管服务',
  },
  {
    id: '钱包地址', en: 'Wallet Address', category: 'tech',
    def: '区块链账户的公开标识符，由公钥哈希派生，格式如"0x742d35Cc6634C0532925a3b844Bc454e4438f44e"（以太坊40位十六进制）。钱包地址是RWA代币的"收款账号"，是KYC白名单系统的核心登记标识。在合规RWA系统中，只有通过KYC认证并被加入白名单的钱包地址才能接收、持有和转让代币。',
    scenario: '投资者完成KYC认证（护照+银行流水+资产证明）后，其MetaMask钱包地址（0x742d...）被发行方的合规系统加入白名单合约。此后该地址可接收RWA代币转账；未在白名单中的任何地址尝试接收该RWA代币，智能合约自动拒绝转账——全程无需人工审核。',
    compliance: '白名单机制是证券型代币合规流通的核心控制。发行方须建立白名单维护流程，定期清理失效地址。',
    example: 'Securitize、Tokeny（T-REX协议）均内置白名单智能合约管理系统',
  },
  // ── 合规与监管 ──
  {
    id: 'KYC/AML', en: 'Know Your Customer / Anti-Money Laundering', category: 'compliance',
    def: '了解你的客户/反洗钱。RWA合规发行的基础要求。KYC核查投资者身份（护照/ID、地址证明、资金来源）；AML筛查PEP（政治敏感人士）、OFAC制裁名单，预防洗钱犯罪。',
    scenario: '香港RWA平台要求投资者上传护照、近3个月银行流水、资产证明（≥800万港币），经Jumio等服务商验证后才能将钱包地址加入白名单。',
    compliance: '未完成KYC即认购RWA代币属非法，香港VATP须对每位客户进行充分KYC，并定期复查。违规将被SFC吊销牌照。',
    example: 'HashKey Exchange、OSL均与Sumsub、Jumio等KYC服务商合作建立合规级KYC体系',
  },
  {
    id: 'FATF旅行规则', en: 'FATF Travel Rule', category: 'compliance',
    def: '金融行动特别工作组（FATF）规则，要求虚拟资产服务提供商在转账超过1000美元/欧元时，须随交易传递发送方和接收方的身份信息。这是全球虚拟资产AML合规的核心规则。',
    scenario: '投资者从交易所A向交易所B转移价值5000美元的RWA代币，交易所A须将投资者姓名、账户ID、钱包地址等信息传送给交易所B，确保资金追踪可溯源。',
    compliance: '香港SFC要求所有VATP遵守旅行规则，使用合规的Travel Rule解决方案（如Notabene、VerifyVASP、TRM Labs）。不合规将被SFC处罚。',
    example: 'Notabene、VerifyVASP是香港VATP广泛使用的旅行规则合规技术服务商',
  },
  {
    id: '白名单', en: 'Whitelist', category: 'compliance',
    def: 'RWA合规体系中，通过KYC/AML审查的合格投资者钱包地址列表。只有白名单内的地址才能持有、接收或转让证券型代币，通常写入智能合约自动执行。',
    scenario: '某RWA代币智能合约设定：每笔转账须检查接收方是否在白名单，若不在则自动拒绝转账，确保代币始终在合规投资者之间流通。',
    compliance: '白名单机制是证券型代币合规流通的核心控制。发行方须建立白名单维护流程，定期清理失效地址。',
    example: 'Securitize、Tokeny（T-REX协议）均内置白名单智能合约管理系统',
  },
  {
    id: 'MiCA', en: 'Markets in Crypto-Assets Regulation', category: 'compliance',
    def: '欧盟加密资产市场监管条例。2024年12月在欧盟27国全面生效，为加密资产建立统一监管框架。注意：证券型RWA代币不纳入MiCA，须在MiFID II框架下监管。',
    scenario: '欧洲公司在MiCA框架下发行稳定币（ART/EMT），须向母国监管机构申请CASP牌照，满足托管、披露、AML要求。证券型RWA则走MiFID II路径。',
    compliance: 'MiCA与MiFID II双轨并行，须经法律意见书确认代币定性后选择正确合规路径。选错路径将构成监管违规。',
    example: 'ING银行、Deutsche Bank等欧洲金融机构已启动MiCA合规架构搭建',
  },
  {
    id: 'Reg D 506(c)', en: 'SEC Regulation D Rule 506(c)', category: 'compliance',
    def: '美国SEC注册豁免条款。允许在不经完整SEC注册的情况下发行证券，仅限合格投资者（个人年收入≥20万美元或净资产≥100万美元），无募资金额上限，可公开宣传。是美国RWA最常用的合规路径。',
    scenario: 'Ondo Finance、Maple Finance等美国RWA项目均采用Reg D 506(c)豁免，通过白名单KYC限制认购者为合格投资者，同时可在官网公开宣传产品。',
    compliance: 'Reg D 506(c)豁免须在募资结束后15天内向SEC提交Form D。禁止向非合格投资者出售，否则构成欺诈。',
    example: 'Ondo Finance OUSG、Centrifuge、RealT均采用Reg D 506(c)路径在美国合规运营',
  },
  {
    id: '尽职调查', en: 'Due Diligence (DD)', category: 'compliance',
    def: '对底层资产、发行人、SPV结构、法律合规性进行的系统性全面核查。RWA发行须完成法律DD（律师）+财务DD（会计师）+技术DD（区块链审计）三层尽职调查，结果须向投资者充分披露。',
    scenario: '投资者委托律所对某RWA房产项目进行DD：核查产权证、租约、SPV股权结构、智能合约代码、KYC流程合规性，出具DD报告作为投资决策依据。',
    compliance: '发行方对虚假陈述或关键信息遗漏须承担民事和刑事责任。投资者有权要求提供完整DD报告，发行方不得拒绝。',
    example: '国际四大（PWC、KPMG、Deloitte、EY）均有RWA/数字资产DD服务',
  },
  {
    id: '托管人制度', en: 'Custodian Arrangement', category: 'compliance',
    def: '独立于发行人的第三方机构，负责保管RWA底层资产及代币私钥的合规安排。香港SFC明确要求：证券型RWA代币的底层资产及代币本身须由独立合规托管人保管，发行人不得自行托管。',
    scenario: '某RWA项目：底层资产（商业地产产权文件）由香港专业信托公司保管；代币私钥由HashKey Custody（持牌托管机构）保管；二者相互独立，双重防护。',
    compliance: 'SFC对托管安排不合规的发行方拒绝批准或吊销牌照。独立托管是防止发行方卷款跑路的核心制度保障，不可省略。',
    example: 'HashKey Custody、BC Group旗下OSL Custody均持有SFC批准的托管资质',
  },
  // ── 机构与角色 ──
  {
    id: 'VASP/VATP', en: 'Virtual Asset Service Provider / Trading Platform', category: 'institution',
    def: 'VASP：虚拟资产服务提供商（广义）；VATP：虚拟资产交易平台（香港特定监管用语）。香港2023年《条例》下须向SFC申领牌照的机构。截至2025年底，香港共有12家获批VATP。',
    scenario: '企业希望在香港发行RWA代币并提供二级市场交易，须选择SFC持牌VATP（如HashKey Exchange、OSL）作为发行和上市平台。',
    compliance: '仅SFC批准的VATP可经营虚拟资产交易业务。自建平台未申牌即运营属违法，最高处500万港元罚款及2年监禁。',
    example: '12家持牌VATP完整清单见本站"香港持牌机构图谱"板块，官网链接一并提供',
  },
  {
    id: 'SPV', en: 'Special Purpose Vehicle', category: 'institution',
    def: '特殊目的载体。发行RWA时常用的离岸法律实体（如开曼群岛公司、BVI公司或香港有限合伙），用于隔离底层资产风险、持有基础资产并发行代币。SPV是RWA发行架构的核心法律容器。',
    scenario: '内地房地产商成立开曼SPV，将内地商业地产未来收益权转让给SPV，SPV在香港发行代币，代币持有人实际持有SPV的收益分配权。',
    compliance: 'SPV须由香港律师出具法律意见书，确认资产权属清晰、转让合法。开曼/BVI公司须年度审计和申报，满足实质运营要求。',
    example: '全球90%+的RWA项目使用开曼群岛SPV结构，设立成本约15-30万港元',
  },
  {
    id: '专业投资者', en: 'Professional Investor (PI)', category: 'institution',
    def: '香港监管概念。个人须满足：净资产≥800万港元（含主要居所，但不超过500万）或受管理投资组合≥800万港元；机构须满足资产≥400万港元。只有专业投资者才能认购香港合规框架下的RWA/STO产品。',
    scenario: '内地高净值个人（金融资产≥1100万人民币，折合约800万港元）可通过香港银行开设PI账户，认购HashKey发行的RWA代币。',
    compliance: '发行方须保存每位投资者的PI认定文件至少7年。向非PI销售证券型代币属严重违规，可被SFC撤销牌照并追究刑事责任。',
    example: 'SFC《证券及期货条例》附表1第1部完整定义，可在SFC官网查询',
  },
  {
    id: '做市商', en: 'Market Maker', category: 'institution',
    def: 'RWA二级市场流动性提供者。通过持续报出买卖双向价格，为投资者提供随时买卖通道。机构做市商（如Jane Street、Cumberland DRW）的参与是RWA代币二级市场成熟的标志。',
    scenario: '某RWA代币在HashKey Exchange上市，做市商报出买入价99.8港元、卖出价100.2港元，bid-ask spread仅0.4%，使投资者可随时以合理价格进出。',
    compliance: '在SFC持牌VATP上进行做市活动须符合SFC对操纵市场的禁止规定，不得进行虚假交易（wash trading）。',
    example: 'Cumberland DRW、GSR Markets是加密/RWA市场主要机构做市商',
  },
  // ── 金融工具 ──
  {
    id: 'CBDC', en: 'Central Bank Digital Currency', category: 'finance',
    def: '央行数字货币。由中央银行发行的法定数字货币，具有国家主权信用背书。批发型CBDC（机构间结算用）和零售型CBDC（公众使用）是两大方向。香港HKMA正测试批发型e-HKD用于RWA结算。',
    scenario: '银行A通过HKMA批发型e-HKD系统向银行B完成1亿港元RWA代币的T+0原子结算，彻底消除传统T+2结算中的信用风险。',
    compliance: '批发型CBDC仅供持牌银行使用，不对公众开放。中国e-CNY目前主要用于零售支付，与RWA结算路径尚未打通。',
    example: 'HKMA Project Ensemble测试批发e-HKD，欧洲央行测试批发数字欧元',
  },
  {
    id: 'NAV', en: 'Net Asset Value', category: 'finance',
    def: '资产净值。RWA基金型产品的每单位代币价值，等于总资产减去负债后除以代币总数量。NAV须定期由独立估值机构计算并披露，是RWA代币的定价基准。',
    scenario: 'Ondo Finance的OUSG代币每日公布NAV：底层资产（美国短期国债ETF）总市值减管理费后除以流通代币总量，即每枚代币赎回价值。',
    compliance: 'NAV须由独立审计机构（如Big4）核实，至少每月披露一次。NAV操纵属欺诈行为，须承担刑事责任。',
    example: 'Franklin Templeton BENJI代币NAV每日更新，可在官网实时查询',
  },
  {
    id: 'ABS', en: 'Asset-Backed Securities', category: 'finance',
    def: '资产支持证券。以底层资产产生的现金流为支撑发行的证券，是RWA最接近的传统金融产品形态。RWA本质上是ABS在区块链上的数字化升级版，具有更高的流动性、更低的发行门槛和可编程性。',
    scenario: '银行将1000笔汽车贷款打包成ABS，通过SPV发行到资本市场。RWA则是将同样的底层资产代币化，在区块链上发行，投资者可从1万元起购。',
    compliance: '境内ABS受证监会和银保监会监管，在交易所或银行间市场发行；RWA（代币化ABS）须经香港持牌机构发行，不可在境内发行。',
    example: '中国境内ABS市场年发行规模超2万亿元，是RWA的重要底层资产来源池',
  },
  {
    id: 'REITs', en: 'Real Estate Investment Trusts', category: 'finance',
    def: '房地产投资信托基金。通过公募募集资金，投资于商业地产并以租金/升值收益为投资者分红的金融工具。代币化REITs（Token REITs）是当前最热门的RWA类型之一，可降低投资门槛至几千元。',
    scenario: 'RealT将底特律商业地产代币化为Token REITs，投资者可从50美元起购，每周自动通过智能合约分配租金，真正实现"碎片化"房产投资。',
    compliance: '香港持牌REITs须经香港证监会批准，代币化REITs须在SFC持牌框架下发行，须满足"房地产资产≥90%NAV"等基础要求。',
    example: 'RealT（美国）、Brickken（欧洲）是代币化房产REITs的标杆项目',
  },
  {
    id: '碳信用', en: 'Carbon Credit', category: 'finance',
    def: '经国际标准机构（Verra VCS、Gold Standard等）核证的温室气体减排量，1个碳信用代表减少或避免1吨CO₂当量的排放。代币化碳信用可在链上透明交易和注销，提升碳市场效率。',
    scenario: '亚马逊雨林保护项目产生100万吨REDD+碳信用，经Verra核证后由Toucan Protocol代币化为BCT代币，企业购买BCT用于抵消碳排放并链上注销。',
    compliance: '碳信用须来自Verra VCS、Gold Standard等国际认可机构。代币化须确保底层碳信用已被注销（避免"租而不销"的双重计算问题），注销凭证须链上存证。',
    example: 'Toucan Protocol已代币化超1500万吨碳信用，KlimaDAO持有大量BCT',
  },
  {
    id: 'TVL', en: 'Total Value Locked', category: 'finance',
    def: '链上锁定总价值。衡量DeFi协议或RWA平台中锁定资产规模的指标，体现协议的规模、市场信任度和流动性深度。TVL是评估RWA平台实力的重要指标之一。',
    scenario: 'Ondo Finance的TVL = 所有OUSG、USDY等代币的底层资产总市值，2024年突破6亿美元，反映机构对其产品的信任度。',
    compliance: 'TVL本身不受监管，但高TVL平台若发生智能合约漏洞将损失惨重，须定期安全审计。TVL数据来源须透明可查（如DefiLlama）。',
    example: '可在DeFiLlama（defillama.com）实时查询各RWA协议的TVL排名',
  },
  {
    id: '信托受益凭证', en: 'Trust Beneficiary Certificate', category: 'finance',
    def: '受益人持有的证明其对信托财产享有受益权的凭证。在某些RWA架构中，代币代表信托受益凭证，持有人通过信托结构间接享有对底层资产现金流的分配权，同时受信托法保护。',
    scenario: '某香港信托公司持有内地商业地产收益权，发行对应的信托受益凭证（代币化），代币持有人依《信托法》享有受益权，发行人破产时信托资产独立存在。',
    compliance: '信托结构须在合法司法管辖区（香港、开曼）设立，须有独立受托人管理，受托人须为持牌信托公司。',
    example: '部分香港RWA项目采用有限合伙信托结构发行收益凭证代币',
  },
  {
    id: '供应链金融票据', en: 'Loan Participation Note (LPN)', category: 'finance',
    def: '允许投资者参与特定贷款一部分的金融工具。银行将已发放贷款拆分成多份，通过LPN代币出售给投资者，投资者获得贷款利息分成。是信贷RWA的重要形式，使机构信贷市场对散户和中小投资者开放。',
    scenario: '某银行向大型商业地产开发商发放20亿元贷款，将其中10亿元以LPN形式代币化，分成10000份（每份10万元），通过SFC持牌平台向香港专业投资者发售，投资者获得3.8%/年的贷款利率分成。',
    compliance: 'LPN须满足SFC证券型代币要求，须披露底层贷款的风险（违约率、抵押物状况），须由持牌资产管理人管理信贷池。',
    example: 'Maple Finance（去中心化机构信贷LPN平台，TVL$5亿+）；Goldfinch（新兴市场信贷LPN，服务非洲/拉美SME融资）',
  },
  {
    id: '碳配额与CCER', en: 'Carbon Quota & CCER', category: 'finance',
    def: '碳配额（EUA）：政府分配给高排放企业的许可排放量，超额排放须购买配额；CCER（中国核证自愿减排量）：国内自愿减排项目产生的碳信用，可用于抵消配额缺口。两者代币化是中国碳市场与国际RWA接轨的重要方向。',
    scenario: '某高碳企业在全国碳市场持有100万吨碳配额，将50万吨通过北京绿色交易所代币化，在香港发行给国际ESG投资者，既获得流动资金，又帮助国际机构实现碳抵消目标。',
    compliance: '境内碳配额属国家监管资产，代币化须经生态环境部和证监会双重审批。CCER项目须经官方认证（UNFCCC/生态环境部），代币化须防止双重计算（同一碳信用不能被两个买家都用于抵消）。',
    example: '北京绿色交易所2023年完成首笔碳配额区块链存证；上海环境能源交易所探索CCER链上流转；Toucan Protocol（国际碳信用代币化，已代币化1500万吨+）',
  },
  {
    id: '票据资产池', en: 'Bill/Note Asset Pool', category: 'finance',
    def: '将多张商业票据（银行承兑汇票、商业承兑汇票、电子商业票据）打包成资产池，通过ABS或RWA形式发行给投资者。代币化票据资产池可大幅提升票据市场流动性，是供应链金融RWA的核心形态。',
    scenario: '某供应链金融平台归集500家中小企业持有的共计1亿元银行承兑汇票，在蚂蚁链上进行存证和资产确权，通过香港SPV发行代币化票据ABS，国际投资者购买后按票面利率分配收益，到期自动赎回。',
    compliance: '票据资产池代币化须满足银行承兑汇票的转贴现规定（不得超期）、应收账款转让登记（中登网）、以及香港SFC代币化证券要求。',
    example: '中国银行票据登记系统（ECDS）是境内数字票据基础设施；海尔供应链金融平台票据存证规模超100亿元',
  },
  // ── DeFi
  {
    id: 'DeFi', en: 'Decentralized Finance', category: 'defi',
    def: '去中心化金融。通过区块链智能合约实现借贷、交易、收益耕作等金融功能，无需传统银行等中介。RWA作为稳定收益资产，正成为DeFi协议最重要的抵押品来源，推动TradFi与DeFi融合。',
    scenario: 'MakerDAO将价值2亿美元的美国国债RWA作为DAI稳定币的抵押品，在无需银行的情况下为DAI提供真实世界的稳定收益支撑，使DAI储蓄利率可达4-5%。',
    compliance: 'DeFi本身去中心化，但与受监管的RWA资产结合时，发行方须确保RWA端的合规性。用DeFi做RWA二级市场须符合所在地交易所监管要求。',
    example: 'MakerDAO持有约10亿美元RWA作为DAI储备，Aave引入RWA扩展借贷市场',
  },
  {
    id: '流动性池', en: 'Liquidity Pool', category: 'defi',
    def: 'DeFi协议中由流动性提供者（LP）注入代币建立的储备池，通过算法自动做市（AMM）为代币提供即时兑换服务。RWA代币流动性池可为持有人提供不依赖传统交易所的退出通道。',
    scenario: 'Centrifuge流动性池：LP注入USDC，借款方（发票融资企业）通过抵押应收账款代币借出USDC获融资，LP获利息收益。',
    compliance: '流动性提供在多数司法管辖区无须牌照，但若构成承销证券型代币则需持牌。须视所在地法规和资产性质判断。',
    example: 'Centrifuge Tinlake池、Maple Finance机构借贷池、Goldfinch全球信贷池',
  },
  {
    id: '原子结算', en: 'Atomic Settlement (DVP)', category: 'defi',
    def: '货银两讫原子结算。区块链技术使资产转让和付款在同一笔交易中同步完成，消除传统T+2结算中的结算风险。这是RWA相较传统金融的重大技术优势。',
    scenario: '买方支付1000万港元e-HKD，同时卖方的RWA代币转入买方钱包。两个动作在同一区块中不可分割地完成，任何一方都无法只收款不交货。',
    compliance: '原子结算的前提是买卖双方均在同一区块链生态系统内，须解决跨链互操作性问题。HKMA Project Ensemble正在测试此场景。',
    example: 'HKMA Project Ensemble沙盒的核心测试场景即为批发型CBDC实现RWA原子结算',
  },
  {
    id: 'AMM', en: 'Automated Market Maker', category: 'defi',
    def: '自动化做市商。通过算法（如恒定乘积x*y=k公式）而非传统订单簿来提供流动性，任何人都可以成为流动性提供者（LP）。代表性协议：Uniswap、Curve、Balancer。RWA代币可通过AMM实现7×24小时流动性。',
    scenario: 'Ondo Finance将USDY（美债收益稳定币）引入Uniswap v3 AMM，投资者可随时以接近面值的价格买卖，无需等待传统赎回周期（T+1到T+3）。',
    compliance: 'AMM协议通常不须持牌，但在AMM上交易的证券型代币须符合发行地的证券法。通过AMM规避合规限制的行为可被监管机构追溯。',
    example: 'Uniswap是最大AMM，Curve专注稳定资产兑换，Balancer支持多资产池',
  },
  {
    id: '稳定币', en: 'Stablecoin', category: 'defi',
    def: '与法定货币（通常为美元）挂钩，价格稳定的加密资产。RWA领域主要用作结算货币：投资者用USDC/USDT支付购买RWA代币，或RWA代币产生的收益以稳定币分配。',
    scenario: 'RWA平台使用USDC（Circle发行的美元稳定币）作为认购和分红货币，投资者将银行美元换成USDC后才能参与链上RWA认购。',
    compliance: '境内严禁持有、交易或使用稳定币进行支付。香港合规框架下，稳定币发行须申请HKMA"稳定币发行人牌照"（2024年立法）。',
    example: 'USDC（Circle）、USDT（Tether）是最大稳定币；MakerDAO的DAI是最大去中心化稳定币',
  },
  {
    id: '二级市场流动性', en: 'Secondary Market Liquidity', category: 'defi',
    def: 'RWA代币发行后在持牌交易所或DeFi协议上的流通交易能力。二级市场流动性是投资者退出的核心通道，也是RWA相较传统私募基金的重大优势。流动性不足是当前RWA最大痛点之一。',
    scenario: 'HashKey Exchange为某REITs RWA代币提供二级市场，专业投资者可随时在盘前交易时间挂单买卖，但每日成交量仅数十万港元，流动性有限。',
    compliance: '二级市场交易须在SFC持牌交易所进行，不得在无牌平台交易，不得向非专业投资者提供场外报价。',
    example: 'HashKey Exchange、OSL是目前香港最主要的RWA代币二级市场提供者',
  },
];

// ── Pinyin first-letter map for Chinese terms ──
// Maps term id → pinyin initial letter for index navigation
const PINYIN_MAP: Record<string, string> = {
  // 监管机构
  'SFC': 'S', 'HKMA': 'H', 'CSRC': 'C', 'PBOC': 'P', 'NFRA': 'N',
  'SEC': 'S', 'MAS': 'M', 'ESMA': 'E', 'BIS': 'B',
  // 基础概念
  'RWA': 'R', '代币化': 'D', 'STO': 'S', '底层资产': 'D', '资产确权': 'Z',
  '分级代币': 'F', '真实出售': 'Z',
  // ERC标准
  'ERC-20': 'E', 'ERC-721': 'E', 'ERC-1155': 'E', 'ERC-721A': 'E',
  'ERC-1400': 'E', 'ERC-1404': 'E', 'ERC-1594': 'E', 'ERC-3643': 'E',
  'ERC-3525': 'E', 'ERC-4626': 'E', 'ERC-4907': 'E', 'ERC-777': 'E',
  'ERC-2612': 'E', 'ERC-1363': 'E', 'ERC-2981': 'E', 'ERC-725': 'E',
  'ERC-1238': 'E', 'ERC-2771': 'E',
  // 技术与代币
  '智能合约': 'Z', '联盟链': 'L', '预言机': 'Y', 'Layer2': 'L',
  'DID': 'D', 'ZKP': 'Z', 'EVM': 'E', '哈希值': 'H', '默克尔树': 'M',
  '私钥与公钥': 'S', '钱包地址': 'Q',
  // 合规与监管
  'KYC/AML': 'K', 'FATF旅行规则': 'F', '白名单': 'B', 'MiCA': 'M',
  'Reg D 506(c)': 'R', '尽职调查': 'J', '托管人制度': 'T',
  // 机构与角色
  'VASP/VATP': 'V', 'SPV': 'S', '专业投资者': 'Z', '做市商': 'Z',
  // 金融工具
  'CBDC': 'C', 'NAV': 'N', 'ABS': 'A', 'REITs': 'R', '碳信用': 'T',
  'TVL': 'T', '信托受益凭证': 'X', '供应链金融票据': 'G',
  // DeFi
  'DeFi': 'D', '流动性池': 'L', '原子结算': 'Y', 'AMM': 'A',
  '稳定币': 'W', '二级市场流动性': 'E',
};

function getTermLetter(termId: string): string {
  return PINYIN_MAP[termId] ?? termId[0]?.toUpperCase() ?? '#';
}

// ── Virtual Terms Grid for each category ──
interface VirtualTermsGridProps {
  terms: Term[];
  cat: typeof CATEGORIES[0];
  expanded: string | null;
  onToggle: (id: string) => void;
  t: (zh: string, en: string) => string;
  activeLetter: string | null;
  favoriteIds: string[];
  onToggleFavorite: (id: string, e: React.MouseEvent) => void;
}

function VirtualTermsGrid({ terms, cat, expanded, onToggle, t, activeLetter, favoriteIds, onToggleFavorite }: VirtualTermsGridProps) {
  const { containerRef, progress, isScrollable, isAtBottom } = useScrollProgress();

  const COLS = 3;
  const rows = Math.ceil(terms.length / COLS);

  const virtualizer = useVirtualizer({
    count: rows,
    getScrollElement: () => containerRef.current,
    estimateSize: () => 120,
    overscan: 2,
  });

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div className={`${cat.bg} dark:bg-gray-800/50 p-5`}>
      {/* Progress bar */}
      {isScrollable && (
        <div className="mb-3 flex items-center gap-3">
          <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: `${cat.accent}20` }}>
            <div
              className="h-full rounded-full transition-all duration-150"
              style={{ width: `${progress}%`, background: cat.accent }}
            />
          </div>
          <span className="text-xs whitespace-nowrap shrink-0" style={{ color: `${cat.accent}99` }}>
            {isAtBottom
              ? t('已浏览全部', 'All viewed')
              : `${progress}% · ${t('滚动查看更多', 'Scroll for more')}`}
          </span>
        </div>
      )}

      <div
        ref={containerRef}
        className="relative overflow-auto"
        style={{
          height: terms.length > 6 ? '520px' : 'auto',
          maxHeight: terms.length > 6 ? '520px' : 'none',
          scrollbarWidth: 'thin',
          scrollbarColor: `${cat.accent}40 transparent`,
        }}
      >
        {terms.length <= 6 ? (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
            {terms.map((term) => (
              <TermCard
                key={term.id}
                term={term}
                cat={cat}
                isOpen={expanded === term.id}
                onToggle={() => onToggle(term.id)}
                t={t}
                activeLetter={activeLetter}
                isFavorite={favoriteIds.includes(term.id)}
                onToggleFavorite={onToggleFavorite}
              />
            ))}
          </div>
        ) : (
          <div className="relative" style={{ height: `${virtualizer.getTotalSize()}px` }}>
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
              }}
            >
              {virtualItems.map((virtualRow) => {
                const startIdx = virtualRow.index * COLS;
                const rowTerms = terms.slice(startIdx, startIdx + COLS);
                return (
                  <div
                    key={virtualRow.key}
                    data-index={virtualRow.index}
                    ref={virtualizer.measureElement}
                    className="grid md:grid-cols-2 xl:grid-cols-3 gap-3 mb-3"
                  >
                    {rowTerms.map((term) => (
                      <TermCard
                        key={term.id}
                        term={term}
                        cat={cat}
                        isOpen={expanded === term.id}
                        onToggle={() => onToggle(term.id)}
                        t={t}
                        activeLetter={activeLetter}
                        isFavorite={favoriteIds.includes(term.id)}
                        onToggleFavorite={onToggleFavorite}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Bottom fade */}
      {isScrollable && !isAtBottom && (
        <div
          className="relative -mt-10 h-10 pointer-events-none"
          style={{ background: `linear-gradient(to bottom, transparent, ${cat.bg.includes('gray') ? 'rgb(249,250,251)' : 'white'}CC)` }}
        >
          <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex items-center gap-1 text-xs" style={{ color: `${cat.accent}80` }}>
            <i className="ri-arrow-down-s-line animate-bounce text-sm"></i>
            <span>{t('继续滚动', 'Scroll down')}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Single Term Card ──
interface TermCardProps {
  term: Term;
  cat: typeof CATEGORIES[0];
  isOpen: boolean;
  onToggle: () => void;
  t: (zh: string, en: string) => string;
  activeLetter: string | null;
  isFavorite: boolean;
  onToggleFavorite: (id: string, e: React.MouseEvent) => void;
}

function TermCard({ term, cat, isOpen, onToggle, t, activeLetter, isFavorite, onToggleFavorite }: TermCardProps) {
  const termLetter = getTermLetter(term.id);
  const isHighlighted = activeLetter !== null && termLetter === activeLetter;

  return (
    <div
      onClick={onToggle}
      className={`bg-white dark:bg-gray-800 border rounded-2xl overflow-hidden cursor-pointer transition-all ${
        isOpen ? `${cat.cardBorder} dark:border-gray-600 shadow-sm` : `${cat.cardBorder} dark:border-gray-700 hover:shadow-sm`
      }`}
      style={
        isHighlighted
          ? {
              outline: `2px solid ${cat.accent}`,
              outlineOffset: '2px',
              boxShadow: `0 0 0 4px ${cat.accent}20`,
              animation: 'pulse-highlight 0.6s ease',
            }
          : undefined
      }
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2.5">
          <div className="flex items-center gap-2.5">
            <div
              className="w-8 h-8 flex items-center justify-center rounded-xl shrink-0 relative"
              style={{ background: `${cat.accent}15`, border: `1px solid ${cat.accent}25` }}
            >
              <i className={`${cat.icon} text-sm`} style={{ color: cat.accent }}></i>
              {/* Letter badge */}
              {isHighlighted && (
                <span
                  className="absolute -top-1.5 -right-1.5 w-4 h-4 flex items-center justify-center rounded-full text-white font-black"
                  style={{ fontSize: '9px', background: cat.accent }}
                >
                  {termLetter}
                </span>
              )}
            </div>
            <div>
              <div className="font-black text-gray-800 dark:text-gray-100 text-sm leading-none mb-0.5">{term.id}</div>
              <div className="text-xs text-gray-400 dark:text-gray-500 leading-none truncate max-w-[140px]">{term.en}</div>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={(e) => onToggleFavorite(term.id, e)}
              className="w-6 h-6 flex items-center justify-center rounded-full transition-all cursor-pointer hover:scale-110"
              title={isFavorite ? t('取消收藏', 'Remove from favorites') : t('收藏', 'Add to favorites')}
            >
              <i className={`text-sm ${isFavorite ? 'ri-star-fill text-amber-400' : 'ri-star-line text-gray-300 hover:text-amber-300'}`}></i>
            </button>
            <i className={`text-sm text-gray-400 transition-transform duration-200 ${isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
          </div>
        </div>
        <p className="text-gray-600 dark:text-gray-400 text-xs leading-relaxed line-clamp-2">{term.def}</p>
      </div>

      {isOpen && (
        <div className="border-t dark:border-gray-700 px-4 pb-4 pt-3.5 space-y-3" style={{ borderColor: `${cat.accent}20` }}>
          <div>
            <div className="flex items-center gap-1.5 text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5">
              <i className="ri-play-circle-line" style={{ color: cat.accent }}></i>
              {t('适用场景', 'Use Cases')}
            </div>
            <p className="text-gray-700 dark:text-gray-300 text-xs leading-relaxed rounded-lg p-2.5" style={{ background: `${cat.accent}08` }}>
              {term.scenario}
            </p>
          </div>
          <div>
            <div className="flex items-center gap-1.5 text-xs font-bold text-amber-700 dark:text-amber-400 mb-1.5">
              <i className="ri-shield-check-line text-amber-500"></i>
              {t('合规提示', 'Compliance Notes')}
            </div>
            <p className="text-xs leading-relaxed bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/40 rounded-lg p-2.5 text-amber-800 dark:text-amber-300">
              {term.compliance}
            </p>
          </div>
          <div>
            <div className="flex items-center gap-1.5 text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5">
              <i className="ri-links-line" style={{ color: cat.accent }}></i>
              {t('真实案例', 'Real Cases')}
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-xs leading-relaxed">{term.example}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Recent viewed storage ──
const RECENT_KEY = 'rwa99_glossary_recent';
const MAX_RECENT = 8;

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_e) {
    return [];
  }
}

function saveRecent(ids: string[]) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(ids.slice(0, MAX_RECENT)));
  } catch (_e) {
    // ignore
  }
}

// ── Favorites storage ──
const FAV_KEY = 'rwa99_glossary_favorites';

function loadFavorites(): string[] {
  try {
    const raw = localStorage.getItem(FAV_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_e) {
    return [];
  }
}

function saveFavorites(ids: string[]) {
  try {
    localStorage.setItem(FAV_KEY, JSON.stringify(ids));
  } catch (_e) {
    // ignore
  }
}

export default function Glossary() {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [activeLetter, setActiveLetter] = useState<string | null>(null);
  const [recentIds, setRecentIds] = useState<string[]>(() => loadRecent());
  const [showRecent, setShowRecent] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState<string[]>(() => loadFavorites());
  const [showFavorites, setShowFavorites] = useState(false);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const termRefs = useRef<Record<string, HTMLElement | null>>({});
  const { t, lang } = useLang();

  const toggleFavorite = useCallback((termId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setFavoriteIds((prev) => {
      const next = prev.includes(termId)
        ? prev.filter((id) => id !== termId)
        : [termId, ...prev];
      saveFavorites(next);
      return next;
    });
  }, []);

  const clearFavorites = useCallback(() => {
    saveFavorites([]);
    setFavoriteIds([]);
  }, []);

  // Export favorites as plain text
  const exportAsText = useCallback(() => {
    const terms = favoriteIds
      .map((id) => TERMS.find((tt) => tt.id === id))
      .filter(Boolean) as typeof TERMS;
    if (terms.length === 0) return;
    const lines = terms.map((term) => {
      const cat = CATEGORIES.find((c) => c.id === term.category);
      return [
        `【${term.id}】${term.en}`,
        `分类：${cat?.name ?? term.category}`,
        `定义：${term.def}`,
        `适用场景：${term.scenario}`,
        `合规提示：${term.compliance}`,
        `真实案例：${term.example}`,
        '---',
      ].join('\n');
    });
    const content = `RWA词条收藏导出\n导出时间：${new Date().toLocaleString('zh-CN')}\n共 ${terms.length} 个词条\n${'='.repeat(40)}\n\n${lines.join('\n')}`;
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `RWA词条收藏_${new Date().toLocaleDateString('zh-CN').replace(/\//g, '-')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }, [favoriteIds]);

  // Copy favorites to clipboard
  const copyToClipboard = useCallback(async () => {
    const terms = favoriteIds
      .map((id) => TERMS.find((tt) => tt.id === id))
      .filter(Boolean) as typeof TERMS;
    if (terms.length === 0) return;
    const lines = terms.map((term) =>
      `【${term.id}】${term.en}\n定义：${term.def}\n合规提示：${term.compliance}`
    );
    const content = lines.join('\n\n---\n\n');
    try {
      await navigator.clipboard.writeText(content);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (_e) {
      // fallback
    }
  }, [favoriteIds]);

  const [copySuccess, setCopySuccess] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);

  const scrollTo = (id: string) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Record a term view in recent history
  const recordRecent = useCallback((termId: string) => {
    setRecentIds((prev) => {
      const next = [termId, ...prev.filter((id) => id !== termId)].slice(0, MAX_RECENT);
      saveRecent(next);
      return next;
    });
  }, []);

  const handleFavoriteClick = useCallback((termId: string) => {
    const term = TERMS.find((tt) => tt.id === termId);
    if (!term) return;
    setExpanded(termId);
    recordRecent(termId);
    const panelEl = sectionRefs.current[term.category];
    if (panelEl) {
      panelEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [recordRecent]);

  const handleToggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = prev === id ? null : id;
      if (next) recordRecent(id);
      return next;
    });
  }, [recordRecent]);

  // Jump to a recent term: expand it and scroll to its category
  const handleRecentClick = useCallback((termId: string) => {
    const term = TERMS.find((t) => t.id === termId);
    if (!term) return;
    setExpanded(termId);
    recordRecent(termId);
    // Scroll to category panel
    const panelEl = sectionRefs.current[term.category];
    if (panelEl) {
      panelEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [recordRecent]);

  const clearRecent = useCallback(() => {
    localStorage.removeItem(RECENT_KEY);
    setRecentIds([]);
  }, []);

  // Get recent term objects for display
  const recentTerms = useMemo(() =>
    recentIds
      .map((id) => TERMS.find((t) => t.id === id))
      .filter(Boolean) as typeof TERMS,
    [recentIds]
  );

  // Get favorite term objects for display
  const favoriteTerms = useMemo(() =>
    favoriteIds
      .map((id) => TERMS.find((t) => t.id === id))
      .filter(Boolean) as typeof TERMS,
    [favoriteIds]
  );

  const filteredByCategory = useCallback((catId: string) =>
    TERMS.filter((term) => {
      if (term.category !== catId) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        term.id.toLowerCase().includes(q) ||
        term.en.toLowerCase().includes(q) ||
        term.def.includes(search) ||
        term.scenario.includes(search)
      );
    }), [search]);

  const totalVisible = CATEGORIES.reduce((acc, cat) => acc + filteredByCategory(cat.id).length, 0);

  // Build letter index from all visible terms
  const letterIndex = useMemo(() => {
    const map: Record<string, { termId: string; catId: string }[]> = {};
    CATEGORIES.forEach((cat) => {
      filteredByCategory(cat.id).forEach((term) => {
        const letter = getTermLetter(term.id);
        if (!map[letter]) map[letter] = [];
        map[letter].push({ termId: term.id, catId: cat.id });
      });
    });
    return map;
  }, [filteredByCategory]);

  const availableLetters = useMemo(() =>
    'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').filter((l) => letterIndex[l]?.length > 0),
    [letterIndex]
  );

  const handleLetterClick = useCallback((letter: string) => {
    setActiveLetter(letter);
    const entries = letterIndex[letter];
    if (!entries?.length) return;
    // Find the first term with this letter and scroll to its category panel
    const { catId } = entries[0];
    const panelEl = sectionRefs.current[catId];
    if (panelEl) {
      panelEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    // Clear highlight after 2s
    setTimeout(() => setActiveLetter(null), 2000);
  }, [letterIndex]);

  return (
    <section id="glossary" className="py-20 bg-gray-50 dark:bg-gray-900">
      {showQuiz && <GlossaryQuiz onClose={() => setShowQuiz(false)} />}
      <div className="max-w-[1440px] mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gray-800/10 dark:bg-white/10 rounded-full mb-4">
            <i className="ri-book-2-line text-gray-700 dark:text-gray-300 text-sm"></i>
            <span className="text-gray-700 dark:text-gray-300 text-sm font-semibold">{t('11 · RWA全名词百科', '11 · RWA Complete Glossary')}</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-black text-gray-900 dark:text-white mb-4">{t('RWA 全名词百科', 'RWA Complete Glossary')}</h2>
          <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto text-base">
            {t(
              '70+ RWA核心词条 · 8大专题色块 · 每词含定义 · 场景 · 合规提示 · 真实案例 · 持续更新',
              '70+ RWA core terms · 8 themed categories · Each term includes definition · scenario · compliance notes · real cases · continuously updated'
            )}
          </p>
          <button
            onClick={() => setShowQuiz(true)}
            className="mt-4 inline-flex items-center gap-2 px-5 py-2 bg-gray-800 dark:bg-gray-700 text-white rounded-full text-sm font-semibold hover:bg-gray-700 dark:hover:bg-gray-600 cursor-pointer transition-all whitespace-nowrap"
          >
            <i className="ri-brain-line text-sm"></i>
            词条知识测试
            <span className="text-xs opacity-60">20题题库 · 含解析</span>
          </button>
        </div>

        {/* ── Recent Viewed Bar ── */}
        {recentTerms.length > 0 && (
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2.5">
              <button
                onClick={() => setShowRecent((v) => !v)}
                className="flex items-center gap-1.5 text-sm font-bold text-gray-700 dark:text-gray-300 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <i className="ri-time-line text-gray-500 dark:text-gray-400 text-sm"></i>
                {t('最近浏览', 'Recently Viewed')}
                <span className="text-xs text-gray-400 font-normal">({recentTerms.length})</span>
                <i className={`text-xs text-gray-400 transition-transform ${showRecent ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
              </button>
              <button
                onClick={clearRecent}
                className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer transition-colors flex items-center gap-1"
              >
                <i className="ri-delete-bin-6-line text-xs"></i>
                {t('清除记录', 'Clear')}
              </button>
            </div>

            {showRecent && (
              <div className="flex flex-wrap gap-2">
                {recentTerms.map((term) => {
                  const cat = CATEGORIES.find((c) => c.id === term.category)!;
                  return (
                    <button
                      key={term.id}
                      onClick={() => handleRecentClick(term.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-all hover:scale-105 whitespace-nowrap"
                      style={{
                        background: `${cat.accent}12`,
                        border: `1px solid ${cat.accent}30`,
                        color: cat.accent,
                      }}
                    >
                      <i className={`${cat.icon} text-xs`}></i>
                      {term.id}
                      <span className="text-gray-400 font-normal hidden sm:inline truncate max-w-[100px]">{term.en}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Favorites Bar ── */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2.5">
            <button
              onClick={() => setShowFavorites((v) => !v)}
              className="flex items-center gap-1.5 text-sm font-bold text-gray-700 dark:text-gray-300 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <i className={`text-sm ${favoriteTerms.length > 0 ? 'ri-star-fill text-amber-400' : 'ri-star-line text-gray-400'}`}></i>
              {t('我的收藏', 'My Favorites')}
              <span className="text-xs text-gray-400 font-normal">({favoriteTerms.length})</span>
              {favoriteTerms.length > 0 && (
                <i className={`text-xs text-gray-400 transition-transform ${showFavorites ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
              )}
            </button>
            {favoriteTerms.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={copyToClipboard}
                  className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer transition-colors flex items-center gap-1 whitespace-nowrap"
                  title={t('复制为文本', 'Copy as text')}
                >
                  <i className={`text-xs ${copySuccess ? 'ri-checkbox-circle-fill text-green-500' : 'ri-clipboard-line'}`}></i>
                  {copySuccess ? t('已复制！', 'Copied!') : t('复制', 'Copy')}
                </button>
                <button
                  onClick={exportAsText}
                  className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer transition-colors flex items-center gap-1 whitespace-nowrap"
                  title={t('导出为TXT文件', 'Export as TXT')}
                >
                  <i className="ri-download-line text-xs"></i>
                  {t('导出TXT', 'Export TXT')}
                </button>
                <button
                  onClick={clearFavorites}
                  className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer transition-colors flex items-center gap-1 whitespace-nowrap"
                >
                  <i className="ri-delete-bin-6-line text-xs"></i>
                  {t('清空', 'Clear')}
                </button>
              </div>
            )}
          </div>

          {favoriteTerms.length === 0 && (
            <p className="text-xs text-gray-400 italic">
              {t('点击词条右上角的 ☆ 收藏重要词条，方便快速访问', 'Click ☆ on any term card to bookmark it for quick access')}
            </p>
          )}

          {showFavorites && favoriteTerms.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {favoriteTerms.map((term) => {
                const cat = CATEGORIES.find((c) => c.id === term.category)!;
                return (
                  <button
                    key={term.id}
                    onClick={() => handleFavoriteClick(term.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-all hover:scale-105 whitespace-nowrap"
                    style={{
                      background: `${cat.accent}12`,
                      border: `1px solid ${cat.accent}40`,
                      color: cat.accent,
                    }}
                  >
                    <i className="ri-star-fill text-amber-400 text-xs"></i>
                    {term.id}
                    <span className="text-gray-400 font-normal hidden sm:inline truncate max-w-[100px]">{term.en}</span>
                    <span
                      onClick={(e) => toggleFavorite(term.id, e)}
                      className="ml-0.5 text-gray-300 hover:text-red-400 transition-colors cursor-pointer"
                      title={t('取消收藏', 'Remove')}
                    >
                      <i className="ri-close-line text-xs"></i>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Search */}
        <div className="flex justify-center mb-4">
          <div className="relative w-full max-w-lg">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('搜索词条名称、英文名、定义内容...', 'Search term name, English name, definition...')}
              className="w-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 rounded-full px-5 py-3 pl-11 text-sm focus:outline-none focus:border-gray-400 dark:focus:border-gray-500 transition-colors shadow-sm"
            />
            <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-search-line text-sm"></i>
            </div>
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            )}
          </div>
        </div>

        {/* ── Letter Index Bar ── */}
        {availableLetters.length > 0 && (
          <div className="flex flex-wrap justify-center gap-1 mb-6">
            <span className="text-xs text-gray-400 self-center mr-1 whitespace-nowrap">
              {t('首字母跳转：', 'Jump to:')}
            </span>
            {availableLetters.map((letter) => {
              const count = letterIndex[letter]?.length ?? 0;
              const isActive = activeLetter === letter;
              return (
                <button
                  key={letter}
                  onClick={() => handleLetterClick(letter)}
                  title={`${count} ${t('个词条', 'terms')}`}
                  className="relative w-7 h-7 flex items-center justify-center rounded-lg text-xs font-bold transition-all cursor-pointer"
                  style={{
                    background: isActive ? '#1F2937' : undefined,
                    color: isActive ? '#fff' : undefined,
                    transform: isActive ? 'scale(1.15)' : 'scale(1)',
                  }}
                >
                  <span className={isActive ? '' : 'text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 w-full h-full flex items-center justify-center rounded-lg'}>
                    {letter}
                  </span>
                  {count > 1 && (
                    <span
                      className="absolute -top-1 -right-1 w-3.5 h-3.5 flex items-center justify-center rounded-full text-white"
                      style={{ fontSize: '8px', background: '#6B7280' }}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Quick Nav */}
        <div className="flex flex-wrap gap-2 justify-center mb-10">
          {CATEGORIES.map((cat) => {
            const count = filteredByCategory(cat.id).length;
            if (count === 0) return null;
            return (
              <button
                key={cat.id}
                onClick={() => scrollTo(cat.id)}
                className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap text-white shadow-sm hover:opacity-90"
                style={{ background: `linear-gradient(135deg, ${cat.color}, ${cat.color}BB)` }}
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.name}
                <span className="bg-white/25 text-white text-xs px-1.5 py-0.5 rounded-full">{count}</span>
              </button>
            );
          })}
        </div>

        {/* Category Panels */}
        <div className="space-y-10">
          {CATEGORIES.map((cat) => {
            const terms = filteredByCategory(cat.id);
            if (terms.length === 0) return null;

            return (
              <div
                key={cat.id}
                id={`panel-${cat.id}`}
                ref={(el) => { sectionRefs.current[cat.id] = el; }}
                className={`rounded-3xl overflow-hidden border ${cat.border} dark:border-gray-700`}
              >
                {/* Panel Header */}
                <div className={`bg-gradient-to-r ${cat.headerGrad} px-6 py-5`}>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 flex items-center justify-center bg-white/20 rounded-2xl">
                        <i className={`${cat.icon} text-white text-xl`}></i>
                      </div>
                      <div>
                        <h3 className="text-white font-black text-xl leading-none mb-1">{cat.name}</h3>
                        <p className="text-white/75 text-sm">{cat.desc}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="bg-white/20 text-white text-sm font-bold px-3 py-1 rounded-full">
                        {terms.length} {t('个词条', 'Terms')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* ERC Special Overview Panel */}
                {cat.id === 'erc' && !search && (
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 border-b border-indigo-200/60 dark:border-indigo-800/40 p-5">
                    <div className="grid lg:grid-cols-2 gap-5">
                      {/* What + Why */}
                      <div>
                        <div className="text-xs font-bold text-indigo-700 dark:text-indigo-400 mb-2 flex items-center gap-1.5">
                          <i className="ri-information-line"></i>{t('ERC 是什么', 'What is ERC')}
                        </div>
                        <p className="text-gray-700 dark:text-gray-300 text-xs leading-relaxed bg-white dark:bg-gray-800 rounded-xl p-3.5 border border-indigo-100 dark:border-indigo-800/40 mb-3">{ERC_OVERVIEW.what}</p>
                        <div className="grid grid-cols-2 gap-2">
                          {ERC_OVERVIEW.why.map((w) => (
                            <div key={w.title} className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-indigo-100 dark:border-indigo-800/40 flex items-start gap-2">
                              <div className="w-6 h-6 flex items-center justify-center rounded-lg shrink-0 mt-0.5" style={{ background: w.color + '20' }}>
                                <i className={`${w.icon} text-xs`} style={{ color: w.color }}></i>
                              </div>
                              <div>
                                <div className="font-bold text-gray-800 dark:text-gray-200 text-xs mb-0.5">{w.title}</div>
                                <div className="text-gray-500 dark:text-gray-400 text-xs leading-relaxed">{w.desc}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      {/* Matrix */}
                      <div>
                        <div className="text-xs font-bold text-indigo-700 dark:text-indigo-400 mb-2 flex items-center gap-1.5">
                          <i className="ri-table-2"></i>{t('RWA选型速查矩阵', 'RWA Selection Matrix')}
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-xl border border-indigo-100/60 dark:border-indigo-800/40 overflow-hidden">
                          <table className="w-full text-xs">
                            <thead className="bg-indigo-50 dark:bg-indigo-900/30">
                              <tr>
                                <th className="text-left py-2 px-3 text-indigo-700 dark:text-indigo-400 font-bold">{t('标准', 'Standard')}</th>
                                <th className="text-left py-2 px-3 text-indigo-700 dark:text-indigo-400 font-bold">{t('定义', 'Definition')}</th>
                                <th className="text-left py-2 px-3 text-indigo-700 dark:text-indigo-400 font-bold hidden sm:table-cell">{t('应用场景', 'Use Case')}</th>
                                <th className="text-center py-2 px-3 text-indigo-700 dark:text-indigo-400 font-bold">{t('推荐度', 'Rating')}</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-indigo-50 dark:divide-indigo-900/30">
                              {ERC_OVERVIEW.matrix.map((row, i) => (
                                <tr key={i} className="hover:bg-indigo-50/50 dark:hover:bg-indigo-900/20 transition-colors">
                                  <td className="py-2 px-3 font-bold text-indigo-700 dark:text-indigo-400">{row.erc}</td>
                                  <td className="py-2 px-3 text-gray-700 dark:text-gray-300">{row.desc}</td>
                                  <td className="py-2 px-3 text-gray-500 dark:text-gray-400 hidden sm:table-cell">{row.use}</td>
                                  <td className="py-2 px-3 text-center text-amber-500 text-xs">{row.rwa}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="mt-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200/60 dark:border-amber-800/40 rounded-xl p-3">
                          <div className="text-xs font-bold text-amber-800 dark:text-amber-400 mb-2 flex items-center gap-1.5">
                            <i className="ri-lightbulb-flash-line text-amber-500"></i>{t('一句话记住', 'Quick Reference')}
                          </div>
                          <div className="grid grid-cols-2 gap-1.5 text-xs">
                            {[
                              { erc: 'ERC-20', label: '= 钱（可拆分）' },
                              { erc: 'ERC-721', label: '= 唯一物品（NFT）' },
                              { erc: 'ERC-1155', label: '= 钱+物混合' },
                              { erc: 'ERC-3643', label: '= 合规证券（首选）' },
                              { erc: 'ERC-4626', label: '= 理财金库' },
                              { erc: 'ERC-3525', label: '= 分级可拆NFT' },
                            ].map((item) => (
                              <div key={item.erc} className="flex items-center gap-1.5">
                                <span className="font-bold text-amber-800 dark:text-amber-400 whitespace-nowrap">{item.erc}</span>
                                <span className="text-amber-700 dark:text-amber-500">{item.label}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Terms Grid — virtualised */}
                <VirtualTermsGrid
                  terms={terms}
                  cat={cat}
                  expanded={expanded}
                  onToggle={handleToggle}
                  t={t}
                  activeLetter={activeLetter}
                  favoriteIds={favoriteIds}
                  onToggleFavorite={toggleFavorite}
                />
              </div>
            );
          })}
        </div>

        {totalVisible === 0 && (
          <div className="text-center py-16 text-gray-400">
            <i className="ri-search-line text-4xl mb-3 block"></i>
            <p className="text-base">{t('未找到匹配词条，试试其他关键词', 'No matching terms found. Try different keywords.')}</p>
          </div>
        )}

        <div className="mt-8 text-center text-sm text-gray-400 dark:text-gray-500">
          {t('当前显示', 'Showing')} <span className="font-semibold text-gray-600 dark:text-gray-400">{totalVisible}</span> / {TERMS.length} {t('个词条 · 8大专题分类 · 持续补充更新 · 数据更新：2026.03', 'terms · 8 themed categories · continuously updated · Last updated: 2026.03')}
        </div>
      </div>
    </section>
  );
}
